/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import './Login.css';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!username.trim()) {
      setMessage({ type: 'error', text: 'Please enter your username' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.forgotPassword(username.trim());

      if (response.success) {
        setMessage({
          type: 'success',
          text: response.error || 'If an account exists with that username, a password reset email has been sent. Please check your email inbox and spam folder.'
        });
        setUsername('');
      } else {
        setMessage({
          type: 'success', // Still show success to prevent user enumeration
          text: response.error || 'If an account exists with that username, a password reset email has been sent.'
        });
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      // Show success message even on error to prevent user enumeration
      setMessage({
        type: 'success',
        text: 'If an account exists with that username, a password reset email has been sent.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Forgot Password</h2>

        <p className="forgot-password-instructions">
          Enter your username and we'll send you a link to reset your password.
        </p>

        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              placeholder="Enter your username"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="login-links">
          <button
            onClick={() => navigate('/login')}
            className="link-button"
            disabled={isLoading}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
